import React, { useState, useEffect } from "react";
import {
  DocumentTextIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Bars3Icon,
  ChevronDownIcon,
  Bars3BottomLeftIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { fetchDeliveryPartnerOrders } from "@/store/slices/internalUsersSlice";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom"; 
import { Popover } from 'react-tiny-popover';
import { Button } from "@material-tailwind/react";
import api from "@/services/api";
import { handleDownloadURL } from "@/components/DownloadFile";
import { useTranslation } from "react-i18next";

export function SupplierDashboard() {
  const { t } = useTranslation("internal-user");
  const sorting_options = [
      {
          label: t('ascending'),
          value: 'asc'
      },
      {
          label: t('descending'),
          value: 'desc'
      },
  ];
  const [activeTab, setActiveTab] = useState("completed");
  const [sortingOrder, setSortingOrder] = useState('');
  const [isSortingPopoverOpen, setIsSortingPopoverOpen] = useState(false);

  const dispatch = useDispatch();
  const { id } = useParams();

  const {
    delivery_orders,
    delivery_orders_meta,
    delivery_summary,
    list_loading,
  } = useSelector((state) => state.internalUser);

  const apiData = delivery_orders || [];

  const stats = [
    {
      label: t('pending_payments_sar'),
      value: delivery_summary?.payment_pending || 0,
    },
    {
      label: t('completed_jobs'),
      value: delivery_summary?.completed_jobs || 0,
      trend: `${delivery_summary?.progress_percentage || 0}% ${t('vs_last_month')}`,
      trendUp: (delivery_summary?.progress_percentage || 0) >= 0,
    },
    {
      label: t('pending_jobs'),
      value: delivery_summary?.pending_jobs || 0,
    },
    {
      label: t('deductions_sar'),
      value: delivery_summary?.deduction_amount || 0,
      action: "",
    },
    {
      label: t('earned_amount_sar'),
      value: delivery_summary?.total_earned || 0,
    },
  ];

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .replace(",", ".");
  };

  useEffect(() => {
    if (!id) return;

    dispatch(fetchDeliveryPartnerOrders({user_id: id, type: activeTab, page: 1,sort_order: sortingOrder}));

  }, [dispatch, activeTab, id]);

  useEffect(() => {
    dispatch(fetchDeliveryPartnerOrders({user_id: id, type: activeTab, page: 1,sort_order: sortingOrder}));
  }, [sortingOrder]);

  const handelSorting = (value) => {
      setSortingOrder(value);
  }

  const handleDownloadEarnings = async () => {
    try {
      const response = await api.get(`delivery-partner/get-earning-statement?user_id=${id}`);
      
      console.log("Full Response Data:", response.data);

      const pdfUrl = response.data?.data?.file_url || response.data?.file_url;

      if (pdfUrl) {
        handleDownloadURL({ fileUrl: pdfUrl });
        // const link = document.createElement('a');
        // link.href = pdfUrl;
        // link.target = '_blank';
        // link.rel = 'noopener noreferrer';
        // document.body.appendChild(link);
        // link.click();
        // document.body.removeChild(link);
      } else {
        console.error("API response did not contain a file URL", response.data);
      }
    } catch (error) {
      console.error("Failed to download earning statement", error);
    }
  };

  return (
    <div className="min-h-screen text-[#1A1A1A]">
      {/* --- Stat Cards --- */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-10">
        {stats.map((stat, idx) => (
          <div
            key={idx}
            className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm h-36 flex flex-col justify-between"
          >
            <div>
              <h3 className="text-2xl font-bold">{stat.value}</h3>
              <p className="text-sm text-gray-500 mt-1">{stat.label}</p>
            </div>

            {stat.trend && (
              <span
                className={`text-xs font-medium ${
                  stat.trendUp ? "text-green-600" : "text-red-600"
                }`}
              >
                ▲ {stat.trend}
              </span>
            )}

            {stat.action && (
              <button className="text-xs text-red-500 underline font-semibold text-left">
                {stat.action}
              </button>
            )}
          </div>
        ))}
      </div>

      {/* --- Tabs --- */}
      <div className="flex flex-col md:flex-row justify-between items-end mb-4 border-b border-gray-200">
        <div className="flex gap-10">
          <button
            onClick={() => setActiveTab("completed")}
            className={`pb-4 px-1 text-sm font-bold transition-all border-b-2 ${
              activeTab === "completed"
                ? "border-red-500 text-black"
                : "border-transparent text-gray-400"
            }`}
          >
            {t('completed_jobs_tab')} ({delivery_summary?.completed_jobs || 0})
          </button>

          <button
            onClick={() => setActiveTab("pending")}
            className={`pb-4 px-1 text-sm font-bold transition-all border-b-2 ${
              activeTab === "pending"
                ? "border-red-500 text-black"
                : "border-transparent text-gray-400"
            }`}
          >
            {t('pending_jobs_tab')} ({delivery_summary?.pending_jobs || 0})
          </button>
        </div>

        <div className="flex items-center gap-2 pb-3">
           <Popover
              isOpen={isSortingPopoverOpen}
              positions={["bottom"]}
              align="start"
              padding={4}
              onClickOutside={() => setIsSortingPopoverOpen(false)}
              content={
                  <div className="bg-white shadow-xl rounded-md w-48 flex flex-col z-50 border border-gray-100 py-1">
                      <button
                          onClick={() => {
                              handelSorting("");
                              setIsSortingPopoverOpen(false);
                          }}
                          className={`text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2 ${sortingOrder === '' ? 'bg-gray-100 font-semibold' : ''}`}
                      >
                          {t('sort_by_date')}
                      </button>
                      {sorting_options?.map((option) => (
                          <button
                              key={option.value}
                              onClick={() => {
                                  handelSorting(option.value);
                                  setIsSortingPopoverOpen(false);
                              }}
                              className={`text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2 ${sortingOrder === option.value ? 'bg-gray-100 font-semibold' : ''}`}
                          >
                              {option.label}
                          </button>
                      ))}
                  </div>
              }
          >

              <Button
                  onClick={() => setIsSortingPopoverOpen(!isSortingPopoverOpen)}
                  variant="outlined"
                  className="flex items-center gap-2 border-gray-300 text-gray-800 capitalize font-medium"

              >
                  <Bars3BottomLeftIcon className="h-4 w-4" />{sortingOrder !== '' && `(${(sorting_options.find(opt => opt.value === sortingOrder)?.label)?.replace('_', ' ')})`}
              </Button>
          </Popover>
          {/* <button className="flex items-center gap-2 px-4 py-2 bg-[#E9E9E9] rounded-md text-sm font-semibold">
            <FunnelIcon className="w-4 h-4" /> Filter
          </button> */}
          <button className="flex items-center gap-2 px-4 py-2 bg-[#E9E9E9] rounded-md text-sm font-semibold"
          onClick={()=>handleDownloadEarnings()}
          >
            <ArrowDownTrayIcon className="w-4 h-4" /> {t('download_button')}
          </button>
        </div>
      </div>

      {/* --- Table --- */}
      <div className="bg-white rounded-lg overflow-hidden">
        {list_loading ? (
          <div className="p-10 text-center text-gray-400"> <div role="status">
                <svg aria-hidden="true" className="inline w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-green-500" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                    <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                </svg>
            </div>
          </div>
        ) : (
          <table className="w-full text-left">
            <tbody className="divide-y divide-gray-100">
              {apiData.length > 0 ? (
                apiData.map((order) => (
                  <tr
                    key={order.order_name}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="p-5 align-top">
                      <div className="flex flex-col">
                        <span className="text-sm font-extrabold uppercase">
                          {t('order_id')}{" "}
                          {order.order_name}
                        </span>

                        <div className="flex items-center gap-1.5 mt-1">
                          <span className="text-xs text-gray-400 font-medium">
                            {order.purchase_order_number}
                          </span>
                          <a href={order.purchase_order_file_url}>
                            <DocumentTextIcon className="w-4 h-4" />
                          </a>
                        </div>
                      </div>
                    </td>

                    <td className="p-5">
                      <p className="text-[11px] text-gray-400 font-bold">
                        {t('delivery_installation_date')}
                      </p>
                      <p className="text-sm">
                        {formatDate(order.delivery_deadline)}
                      </p>
                    </td>

                    <td className="p-5">
                      <p className="text-[11px] text-gray-400 font-bold">
                        {t('task_type')}
                      </p>
                      <p className="text-sm capitalize">
                        {order.task_type?.replace(/_/g, ' ')}
                      </p>
                    </td>

                    <td className="p-5">
                      <p className="text-[11px] text-gray-400 font-bold">
                        {t('items')}: {order.items_count}
                      </p>
                      <p className="text-sm">{t('deliver_to')}: {order.company_name}</p>
                    </td>

                    {/* <td className="p-5">
                      <p className="text-[11px] text-gray-400 font-bold">
                        Delivery Charge
                      </p>
                      <p className="text-sm">
                        SAR {Number(order.delivery_charge).toLocaleString()}
                      </p>
                    </td> */}

                    <td className="p-5">
                      <p className="text-[11px] text-gray-400 font-bold">
                        {t('delivery_charge')}
                      </p>

                      <div className="flex items-center gap-2">
                        <p className="text-sm">
                          SAR {Number(order.delivery_charge).toLocaleString()}
                        </p>

                        {Number(order.paid_amount || 0) > 0 && (
                          <div className="relative group">
                            <InformationCircleIcon className="w-4 h-4 text-blue-500 cursor-pointer" />

                            <div className="absolute left-1/2 -translate-x-1/2 bottom-6 hidden group-hover:block z-50">
                              <div className="bg-black text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                                {t('paid_amount')}: SAR{" "}
                                {Number(order.paid_amount).toLocaleString()}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>

                    <td className="p-5">
                      <p className="text-[11px] text-gray-400 font-bold">
                        {t('payment_status')}
                      </p>
                      <p className="text-sm capitalize">
                        {order.payment_status}
                      </p>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center py-6 text-gray-400">
                    {t('no_data_found')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* --- Pagination (UI SAME) --- */}
      <div className="flex items-center mt-8 gap-1.5">
        <button className="p-2 border rounded bg-gray-100 text-gray-300">
          <ChevronLeftIcon className="w-4 h-4" />
        </button>

        <button className="px-4 py-2 text-sm font-bold border border-blue-500 text-blue-600 rounded">
          {delivery_orders_meta?.current_page || 1}
        </button>

        <button className="p-2 border rounded bg-white text-gray-600">
          <ChevronRightIcon className="w-4 h-4" />
        </button>

        <div className="flex items-center ml-4">
          <span className="text-sm text-gray-400 ml-3">{t('per_page')}</span>
        </div>
      </div>
    </div>
  );
}

export default SupplierDashboard;